
import { Dialog, DialogContent, Button, Typography, Box, Stack, IconButton } from '@mui/material';
import DialogActionBar from '../../components/DialogActionBar';
import { useUser } from '../../../../hooks/useUser';
import {
  Operation,
  updateOperationOfficials,
  OperationRole,
  OperationAgencyOfficial,
  UpdateOperationOfficialData,
} from '../../../../networkRequest/operationsService';
import { useState, useEffect } from 'react';
import SearchableAgencyOfficialDropdown from '../../../../components/SearchableAgencyOfficialDropdown';
import { Add, Delete } from '@mui/icons-material';
import { OperationAgencyOfficialAttributes } from '@pages/Operations/CreateOperationPage';
import { OPERATION_AGENCY_OFFICIAL_ROLES } from '../../../../constants';

interface EditCommandStaffModalProps {
  open: boolean;
  onClose: () => void;
  operation: Operation;
  onOperationUpdate: (op: Operation) => void;
  operationRoles: OperationRole[];
  commandStaffs: OperationAgencyOfficial[];
}

export default function EditCommandStaffModal({
  open,
  onClose,
  operation,
  onOperationUpdate,
  operationRoles,
  commandStaffs,
}: EditCommandStaffModalProps) {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getEmptyDropdownAgencyOfficial = (): OperationAgencyOfficialAttributes => {
    return {
      official: {
        id: Date.now(),
        first_name: '',
        last_name: '',
      },
      assignment: null,
    };
  };

  const [newCommandStaffList, setNewCommandStaffList] = useState<OperationAgencyOfficialAttributes[]>([]);

  useEffect(() => {
    if (open && commandStaffs) {
      setNewCommandStaffList(commandStaffs.map(oao => {
        return {
          official: oao.agency_official,
          assignment: oao.assignment
        };
      }));
    }
  }, [open, commandStaffs])

  const handleAddCommandStaff = () => {
    setNewCommandStaffList((prevDeputyIncidentCommanders) => {
      // Only allow one empty search input to be added to the end of the list
      if (
        prevDeputyIncidentCommanders.length > 0 &&
        prevDeputyIncidentCommanders[prevDeputyIncidentCommanders.length - 1]?.official?.id === undefined
      ) {
        return prevDeputyIncidentCommanders;
      }
      return [...prevDeputyIncidentCommanders, getEmptyDropdownAgencyOfficial()];
    });
  };

  const handleClearCommandStaff = (id: number) => {
    setNewCommandStaffList((prev) =>
      prev.map((agent) => {
        return agent?.official?.id === id
          ? {
            official: {
              id: Date.now(),
              first_name: '',
              last_name: '',
            },
            assignment: null,
          }
          : agent
      })
    );
  };

  const handleChangeCommandStaff = (id: number, official: OperationAgencyOfficialAttributes | null) => {
    setNewCommandStaffList((prev) =>
      prev.map((agent) => {
        return agent?.official?.id === id
          ? { ...agent, ...official }
          : agent
      })
    );
  };

  const getRoleIdForAssignment = (assignment: string): string => {
    const match = operationRoles.find(
      (role) => role.name.trim().toLowerCase() === assignment.trim().toLowerCase()
    );
    return match ? match.id : '';
  };

  // TO DO: Perform validation
  const isFormValid = () => true

  const handleSave = async () => {
    if (!user?.accessToken || !operation?.id) return;
    if (!isFormValid()) {
      setError(
        // this should never be called since isFormValid only returns true right now.
        'Please correct form errors.'
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let operation_agency_officials: UpdateOperationOfficialData[] = []

      for (const commandStaff of newCommandStaffList) {
        if (commandStaff?.official?.id === undefined) {
          continue;
        }
        let assignment = commandStaff.assignment

        if (!assignment) {
          assignment = "Command Staff"
        } else if (assignment.length <= 0) {
          assignment = "Command Staff"
        }



        operation_agency_officials.push({
          agency_official_id: commandStaff?.official?.id,
          operation_role_id: getRoleIdForAssignment('Command Staff'),
          assignment: assignment
        });
      }

      const newAgencyOfficialIds = new Set<number>(operation_agency_officials.map(a => a.agency_official_id));

      const currentAgencyOfficialIds: number[] = [];

      for (const commandStaff of commandStaffs) {
        currentAgencyOfficialIds.push(commandStaff.agency_official.id);
      }
      const removedCommandStaff: any[] = [];
      for (const id of currentAgencyOfficialIds) {
        if (!newAgencyOfficialIds.has(id)) {
          removedCommandStaff.push(commandStaffs.find(commandStaff => commandStaff.agency_official.id === id));
        }
      }

      for (const official of removedCommandStaff) {
        let assignment = official.assignment
        if (assignment === "Deputy Incident Commander" || assignment === "Incident Commander" || !assignment) {
          assignment = "Participant"
        }
        operation_agency_officials.push({
          agency_official_id: official.agency_official.id,
          operation_role_id: getRoleIdForAssignment('Participant'),
          assignment
        });
      }

      let updateData: UpdateOperationOfficialData[] = operation_agency_officials


      const result = await updateOperationOfficials({
        accessToken: user.accessToken,
        operationId: operation.id,
        operationOfficialData: updateData
      });

      if (result.error) {
        setError(result.error);
        return;
      }

      if (result.data) {
        onOperationUpdate(result.data);
      }

      onClose();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update incident commanders');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
          width: '900px',
          maxWidth: '900px',
          height: 'auto',
          maxHeight: '90vh',
          pt: 4,
          px: 3,
          pb: 2
        }
      }}>
      <DialogContent sx={{ p: 0, mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Edit Command Staff
        </Typography>

        <Box sx={{ background: '#fff', borderRadius: 1, p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Command Staff
          </Typography>
          <Stack spacing={2}>
            {newCommandStaffList.map((agency_official, index) => {
              return (
                <Stack
                  key={index}
                  direction="row"
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <SearchableAgencyOfficialDropdown
                    label="Command Staff"
                    accessToken={user?.accessToken || ''}
                    value={agency_official.official}
                    assignment={agency_official.assignment}
                    onSelect={(official, assignment) => {
                      if (!official || !agency_official.official?.id) return;
                      handleChangeCommandStaff(
                        agency_official.official.id,
                        { official, assignment: assignment ?? OPERATION_AGENCY_OFFICIAL_ROLES.COMMAND_STAFF })
                    }}
                    onError={error => { throw new Error(error) }}
                    hideAssignmentField={false}
                    onClear={handleClearCommandStaff}
                  />

                  <IconButton
                    onClick={() => {
                      setNewCommandStaffList((previousCommandStaff) => {
                        return previousCommandStaff.filter((_, prevIndex) => {
                          return (prevIndex !== index);
                        });
                      });
                    }}
                  >
                    <Delete sx={{ color: (theme) => theme.palette.utilityIcon }} />
                  </IconButton>
                </Stack>
              );
            })}

            <Box>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<Add />}
                onClick={handleAddCommandStaff}
              >
                Command Staff
              </Button>
            </Box>
          </Stack>
        </Box>
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActionBar sx={{ paddingRight: 4 }}>
        <Button onClick={onClose} variant="outlined" size="medium">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="medium"
          disabled={loading || !isFormValid()}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActionBar>
    </Dialog>
  );
}